/**
 * GymLogoPopover Component
 *
 * Displays a gym logo button that opens a popover with gym name,
 * switch gym option, and admin options (settings, create route) when applicable.
 */

import { useState, useEffect, useRef, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

/**
 * Minimal gym data required for the popover
 */
interface GymData {
  id: string;
  name: string;
  logoURL?: string | null;
}

/**
 * Props for GymLogoPopover component
 */
interface GymLogoPopoverProps {
  /** Gym data to display */
  gym: GymData;
  /** Whether user can access gym settings (owners only) */
  canAccessSettings?: boolean;
  /** Whether user can create routes (setters and owners) */
  canCreateRoutes?: boolean;
  /** Use floating action button style (circular with shadow) */
  floatingStyle?: boolean;
}

/**
 * Extracts initials from gym name (first letter of first two words, or first two letters)
 */
const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2 && words[0] && words[1]) {
    return ((words[0][0] ?? '') + (words[1][0] ?? '')).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

/**
 * GymLogoPopover displays a clickable gym logo that opens a popover menu
 *
 * @example
 * ```tsx
 * <GymLogoPopover gym={currentGym} isAdmin={isGymAdmin} />
 * ```
 */
export const GymLogoPopover = ({
  gym,
  canAccessSettings = false,
  canCreateRoutes = false,
  floatingStyle = false,
}: GymLogoPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        popoverRef.current &&
        buttonRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    // Close on Escape key
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div style={containerStyle}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        style={floatingStyle ? floatingLogoButtonStyle : logoButtonStyle}
        aria-label={gym.name}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {gym.logoURL ? (
          <img src={gym.logoURL} alt={`${gym.name} logo`} style={logoImageStyle} />
        ) : (
          <div style={logoPlaceholderStyle}>
            <span style={logoInitialStyle}>{getInitials(gym.name)}</span>
          </div>
        )}
      </button>

      {isOpen && (
        <div ref={popoverRef} style={popoverStyle} role="menu">
          {/* Header with gym name */}
          <div style={popoverHeaderStyle}>
            <span style={gymNameStyle}>{gym.name}</span>
          </div>

          {/* Menu items */}
          <div style={menuItemsStyle}>
            {/* Switch gym - always visible */}
            <Link
              to="/gyms"
              style={menuItemStyle}
              role="menuitem"
              aria-label="Switch gym"
              onClick={() => setIsOpen(false)}
            >
              <SwapHorizIcon fontSize="small" style={iconStyle} />
              <span>Switch gym</span>
            </Link>

            {/* Settings - owners only */}
            {canAccessSettings && (
              <Link
                to={`/gyms/${gym.id}/settings`}
                style={menuItemStyle}
                role="menuitem"
                aria-label="Settings"
                onClick={() => setIsOpen(false)}
              >
                <SettingsIcon fontSize="small" style={iconStyle} />
                <span>Settings</span>
              </Link>
            )}

            {/* Create route - setters and owners */}
            {canCreateRoutes && (
              <Link
                to={`/gyms/${gym.id}/routes/new`}
                style={menuItemStyle}
                role="menuitem"
                aria-label="Create route"
                onClick={() => setIsOpen(false)}
              >
                <AddIcon fontSize="small" style={iconStyle} />
                <span>Create route</span>
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Styles
 */
const containerStyle: CSSProperties = {
  position: 'relative',
  display: 'inline-block',
};

const logoButtonStyle: CSSProperties = {
  width: '40px',
  height: '40px',
  padding: 0,
  border: 'none',
  borderRadius: '50%',
  cursor: 'pointer',
  backgroundColor: 'transparent',
  overflow: 'hidden',
};

const floatingLogoButtonStyle: CSSProperties = {
  width: '48px',
  height: '48px',
  padding: 0,
  border: '1px solid var(--color-border)',
  borderRadius: '50%',
  cursor: 'pointer',
  backgroundColor: 'var(--color-bg-primary)',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
};

const logoImageStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '50%',
};

const logoPlaceholderStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  backgroundColor: 'var(--color-primary)',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const logoInitialStyle: CSSProperties = {
  color: 'white',
  fontSize: '0.875rem',
  fontWeight: 600,
};

const popoverStyle: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  left: 0,
  minWidth: '200px',
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '8px',
  boxShadow: 'var(--shadow-md)',
  border: '1px solid var(--color-border)',
  zIndex: 100,
  overflow: 'hidden',
};

const popoverHeaderStyle: CSSProperties = {
  padding: '12px 16px',
  borderBottom: '1px solid var(--color-border)',
};

const gymNameStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
};

const menuItemsStyle: CSSProperties = {
  padding: '8px 0',
};

const menuItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '10px 16px',
  fontSize: '0.875rem',
  color: 'var(--color-text-primary)',
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'background-color 0.15s',
};

const iconStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
};

export default GymLogoPopover;
